import React, { useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from './ResizableImage';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Image as ImageIcon, Link as LinkIcon, Undo, Redo, Minus, Quote, Upload, CalendarPlus } from 'lucide-react';
import ImageUploader, { uploadFile } from '../ui/ImageUploader';

// Lets a photo be dropped (from Finder) or pasted (from clipboard) straight
// into the text near where the user is working — no trip up to the toolbar
// required. Internal drags (moving an already-inserted image) are left to
// ProseMirror's own node-dragging, not intercepted here.
//
// Image is a block-level node, so inserting it at the raw drop/cursor
// position would split whatever paragraph it lands in — same issue as the
// toolbar insert path. Landing after the enclosing block avoids that.
const blockEndPos = (view, rawPos) => {
  try {
    return view.state.doc.resolve(rawPos).after();
  } catch {
    return rawPos;
  }
};

const insertUploadedFilesAt = async (view, rawPos, files) => {
  let pos = blockEndPos(view, rawPos);
  for (const file of files) {
    try {
      const url = await uploadFile(file);
      const node = view.state.schema.nodes.image.create({ src: url });
      const tr = view.state.tr.insert(pos, node);
      view.dispatch(tr);
      pos += node.nodeSize;
    } catch (err) {
      console.error('Image upload failed:', err);
    }
  }
};

const handleImageDrop = (view, event, slice, moved) => {
  if (moved) return false;
  const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/'));
  if (files.length === 0) return false;
  event.preventDefault();
  const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (!coords) return true;
  insertUploadedFilesAt(view, coords.pos, files);
  return true;
};

const handleImagePaste = (view, event) => {
  const files = Array.from(event.clipboardData?.items || [])
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile());
  if (files.length === 0) return false;
  event.preventDefault();
  insertUploadedFilesAt(view, view.state.selection.from, files);
  return true;
};

// Image is a block-level node, so inserting it at the raw cursor position
// splits whatever paragraph the cursor is in — the image lands wedged
// between two halves of a sentence instead of after it. Inserting after the
// end of the current block avoids that split.
const insertImageAfterBlock = (editor, url) => {
  const { $to } = editor.state.selection;
  try {
    const insertPos = $to.after();
    editor.chain().focus().insertContentAt(insertPos, { type: 'image', attrs: { src: url } }).run();
  } catch {
    // $to.after() throws at document-root depth (no enclosing block) — fall
    // back to inserting at the raw cursor position rather than failing silently.
    editor.chain().focus().setImage({ src: url }).run();
  }
};

function ImagePopover({ editor, onClose }) {
  const [tab, setTab] = useState('upload');
  const [urlValue, setUrlValue] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const insertImage = (url) => {
    insertImageAfterBlock(editor, url);
  };

  const insertImageAndClose = (url) => {
    insertImage(url);
    onClose();
  };

  return (
    <div ref={popoverRef} className="absolute top-full left-0 mt-1 z-50 bg-white rounded-xl shadow-lg border border-platinum-200 p-4 w-80">
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setTab('upload')}
          className={`flex-1 text-sm py-1.5 px-3 rounded-lg font-medium transition-colors ${
            tab === 'upload' ? 'bg-coral-100 text-coral-700' : 'text-platinum-600 hover:bg-platinum-100'
          }`}
        >
          <Upload size={14} className="inline mr-1" /> Upload
        </button>
        <button
          type="button"
          onClick={() => setTab('url')}
          className={`flex-1 text-sm py-1.5 px-3 rounded-lg font-medium transition-colors ${
            tab === 'url' ? 'bg-coral-100 text-coral-700' : 'text-platinum-600 hover:bg-platinum-100'
          }`}
        >
          <LinkIcon size={14} className="inline mr-1" /> URL
        </button>
      </div>

      {tab === 'upload' ? (
        <ImageUploader onUpload={insertImage} crop={false} multiple />
      ) : (
        <div className="space-y-2">
          <input
            type="url"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="w-full px-3 py-2 text-sm border border-platinum-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && urlValue.trim()) {
                insertImageAndClose(urlValue.trim());
              }
            }}
          />
          <button
            type="button"
            onClick={() => urlValue.trim() && insertImageAndClose(urlValue.trim())}
            className="w-full text-sm py-2 bg-coral-400 text-white rounded-lg hover:bg-coral-500 transition-colors font-medium"
          >
            Insert Image
          </button>
        </div>
      )}
    </div>
  );
}

const ToolbarButton = ({ icon: Icon, action, active, title }) => (
  <button
    type="button"
    onClick={action}
    className={`p-2 rounded transition-colors ${
      active
        ? 'bg-coral-100 text-coral-700'
        : 'hover:bg-platinum-200 text-platinum-700'
    }`}
    title={title}
  >
    <Icon size={16} />
  </button>
);

const Divider = () => <div className="w-px h-6 bg-platinum-300 mx-1" />;

const addLinkToEditor = (editor) => {
  const url = window.prompt('Enter URL:');
  if (url) {
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }
};

// Floating toolbar that attaches to the current text selection, so bold/italic/
// headings are reachable without scrolling back up to the fixed menu bar.
function SelectionToolbar({ editor }) {
  if (!editor) return null;

  return (
    <BubbleMenu editor={editor} className="flex items-center gap-0.5 p-1 bg-white rounded-lg shadow-lg border border-platinum-200">
      <ToolbarButton icon={Bold} action={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" />
      <ToolbarButton icon={Italic} action={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" />
      <ToolbarButton icon={Heading2} action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2" />
      <ToolbarButton icon={Heading3} action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3" />
      <Divider />
      <ToolbarButton icon={List} action={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List" />
      <ToolbarButton icon={ListOrdered} action={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List" />
      <Divider />
      <ToolbarButton icon={LinkIcon} action={() => addLinkToEditor(editor)} active={editor.isActive('link')} title="Add Link" />
    </BubbleMenu>
  );
}

function MenuBar({ editor, showDayMarker }) {
  const [showImagePopover, setShowImagePopover] = useState(false);

  if (!editor) return null;

  // Appends a "Day N" heading at the end of the document — the exact marker
  // the save-time splitter looks for — so the day boundary is never typed
  // free-hand and can't drift from the format the parser expects.
  const insertDayMarker = () => {
    let dayCount = 0;
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'heading' && node.attrs.level === 2) dayCount += 1;
    });
    editor.chain().focus('end').insertContent(`<h2>Day ${dayCount + 1}</h2><p></p>`).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-platinum-200 bg-platinum-50 rounded-t-lg">
      {/* Text Style */}
      <ToolbarButton icon={Bold} action={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" />
      <ToolbarButton icon={Italic} action={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" />
      <ToolbarButton icon={Heading2} action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2" />
      <ToolbarButton icon={Heading3} action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3" />

      <Divider />

      {/* Structure */}
      <ToolbarButton icon={List} action={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List" />
      <ToolbarButton icon={ListOrdered} action={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List" />
      <ToolbarButton icon={Quote} action={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote" />
      <ToolbarButton icon={Minus} action={() => editor.chain().focus().setHorizontalRule().run()} active={false} title="Horizontal Rule" />

      {showDayMarker && (
        <>
          <Divider />
          <button
            type="button"
            onClick={insertDayMarker}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium text-coral-700 bg-coral-50 hover:bg-coral-100 transition-colors"
            title="Insert a Day marker at the end"
          >
            <CalendarPlus size={16} />
            Day marker
          </button>
        </>
      )}

      <Divider />

      {/* Media */}
      <div className="relative">
        <ToolbarButton icon={ImageIcon} action={() => setShowImagePopover(!showImagePopover)} active={showImagePopover} title="Add Image" />
        {showImagePopover && (
          <ImagePopover editor={editor} onClose={() => setShowImagePopover(false)} />
        )}
      </div>
      <ToolbarButton icon={LinkIcon} action={() => addLinkToEditor(editor)} active={editor.isActive('link')} title="Add Link" />

      <Divider />

      {/* History */}
      <ToolbarButton icon={Undo} action={() => editor.chain().focus().undo().run()} active={false} title="Undo" />
      <ToolbarButton icon={Redo} action={() => editor.chain().focus().redo().run()} active={false} title="Redo" />
    </div>
  );
}

function RichTextEditor({ content, onChange, placeholder = 'Start writing...', showDayMarker = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      handleDrop: handleImageDrop,
      handlePaste: handleImagePaste,
    },
  });

  return (
    <div className="border border-platinum-200 rounded-lg overflow-hidden">
      <MenuBar editor={editor} showDayMarker={showDayMarker} />
      <SelectionToolbar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[150px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[150px] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-platinum-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:my-4 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_li]:my-1"
      />
    </div>
  );
}

export default RichTextEditor;
